import { FC, useCallback, useEffect, useState } from 'react';
import { WORKFLOW_ACTIONS, Booking, Creator } from '@typings/api-models';
import CheckCircle from '@components/Icons/CheckCircle';
import Collaborators from '@lib/UserWorkflowDetailCustomerView/Collaborators';
import useNoodleApi from '@hooks/useNoodleApi';
import { getUserWorkflowCollaborators } from '@tsClient/workflows';
import { FullWorkflowStep } from '@noodleApi/ApiDefinition/workflows';
import isStepCompleted from '@helpers/isStepCompleted';
import canRetriggerStep from '@helpers/canRetriggerStep';
import CircularProgress from '@components/CircularProgress';
import { useUser } from '@hooks';
import * as format from '@format';
import PageContext from '@components/DesignLibrary/PageContext';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import classNames from 'classnames';
import DocumentsAction from './DocumentsAction';
import classes from './UserWorkflowInfo.module.scss';
import UserWorkflowBooking from './UserWorkflowBooking';

type UserWorkflowInfoProps = {
  compiledDocuments: Parameters<typeof DocumentsAction>[0]['compiledDocuments'] | null;
  userWorkflow:
    & {
      id: string;
      numCompletedTasks: number;
      numTotalTasks: number;
      product?: { title?: string | null } | null;
      workflow: {
        name: string;
        hasGeneratedPdfs: boolean;
      };
      booking?: Pick<Booking, 'id' | 'startAt' | 'endAt' | 'meetingLink'> | null;
    }
    & Parameters<typeof userWorkflowToContext>[0]
    & Parameters<typeof Collaborators>[0]['userWorkflow'];
  hasBorder: boolean;
  collaborators?: Parameters<typeof Collaborators>[0]['collaborators'];
  workflowSteps: FullWorkflowStep[];
  isCreatorView: boolean;
  isCard?: boolean;
  creator: Pick<Creator, 'timezone'> | null;
};

const UserWorkflowInfo: FC<UserWorkflowInfoProps> = ({
  compiledDocuments,
  userWorkflow,
  collaborators,
  hasBorder,
  workflowSteps,
  isCreatorView,
  isCard,
  creator,
}) => {
  const [workflowCollaborators, setWorkflowCollaborators] = useState<UserWorkflowInfoProps['collaborators']>(collaborators);
  const [user] = useUser();
  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(getUserWorkflowCollaborators);

  const handleChangeCollaborator = useCallback((collaborator: Parameters<typeof Collaborators>[0]['collaborators'][number]): void => {
    setWorkflowCollaborators(prev => prev?.map(c => (c.id === collaborator.id ? collaborator : c)));
  }, []);

  useEffect(() => {
    if (loadedCollaborators) {
      setWorkflowCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators]);

  useEffect(() => {
    if (userWorkflow.id) {
      getCollaboratorsFn({ userWorkflowId: userWorkflow.id });
    }
  }, [userWorkflow.id, getCollaboratorsFn]);

  const hasTitle = Boolean(userWorkflow.product?.title);
  const hasDocuments = Boolean(compiledDocuments);
  const generatePdfsTrigger = workflowSteps?.find(wfStep =>
    wfStep.nextSteps.some(nextStep => nextStep.eventName === WORKFLOW_ACTIONS.GENERATE_ALL_WORKFLOW_PDFS),
  );
  const showGeneratePdfsButton = generatePdfsTrigger && isCreatorView
    && (!isStepCompleted(generatePdfsTrigger) || canRetriggerStep(generatePdfsTrigger));
  const showDocumentsDivider = hasTitle && hasDocuments;

  const isUserProfileDetails = !isCreatorView && !isCard;
  const myCollaborator = workflowCollaborators?.find(c => user && c.id === user.id) || null;

  return (
    <div className={hasBorder ? classes.workflowInfo_border : classes.workflowInfo}>
      <div className={classes.stepInfo}>
        <div
          className={classes.workflowInfo_text}
          style={
            (isUserProfileDetails && { margin: 0, marginBottom: 12 })
            || (!isCreatorView && isCard && { margin: 12, maxWidth: 'calc(100% - 80px)' })
            || undefined
          }
        >
          <p className={isCreatorView || isCard ? 'body-sm-bold' : 'heading-sm'}>{userWorkflow.workflow.name}</p>
          <UserWorkflowBooking
            booking={userWorkflow.booking ?? null}
            creator={creator}
          />
          <p className={classes.tasksProgress}>
            {userWorkflow.numCompletedTasks < userWorkflow.numTotalTasks
              ? (
                <CircularProgress
                  size={12}
                  progress={format.percentageComplete({
                    completed: userWorkflow.numCompletedTasks,
                    total: userWorkflow.numTotalTasks,
                  })}
                />
              )
              : (
                <CheckCircle weight="fill" color="var(--color-success)" size={12} />
              )}
            {userWorkflow.numCompletedTasks} of {userWorkflow.numTotalTasks} tasks completed
          </p>
        </div>
      </div>
      {(userWorkflow.product?.title) && (isCreatorView || isCard) && (
        <>
          <div className={classes.workflowExtraInfo}>
            {showDocumentsDivider && (compiledDocuments || showGeneratePdfsButton) && <div className={classes.divider} />}
            {(compiledDocuments || showGeneratePdfsButton) && (
              <>
                <DocumentsAction
                  compiledDocuments={compiledDocuments || []}
                  userWorkflowId={userWorkflow.id}
                  hasGeneratedPdfs={userWorkflow.workflow.hasGeneratedPdfs}
                />
              </>
            )}
          </div>
        </>
      )}
      <div
        className={classNames(classes.contextOverride, !isCard && classes.contextOverrideNotCard)}
      >
        <PageContext
          cantCopy={isCard}
          items={userWorkflowToContext(userWorkflow, !isCreatorView)}
        />
      </div>
      {workflowCollaborators && myCollaborator && !isCard && (
        <Collaborators
          collaborators={workflowCollaborators}
          userWorkflow={userWorkflow}
          myCollaborator={myCollaborator}
          onChangeCollaborator={handleChangeCollaborator}
          isUserProfileDetails={isUserProfileDetails}
        />
      )}
    </div>
  );
};

export default UserWorkflowInfo;
